import React from 'react';
import './formatContent.css';

/**
 * Formats text content with markdown-like syntax support
 * Supports:
 * - Bullet points (lines starting with •)
 * - Bold text (text wrapped in **)
 * - Section headers (full lines wrapped in **)
 * - Line breaks and spacing
 *
 * @param text - The text content to format
 * @returns Array of React elements with formatted content
 */
export const formatContent = (text: string): React.ReactNode[] => {
  const lines = text.split('\n');

  return lines.map((line, index) => {
    // Check if line starts with bullet point
    if (line.startsWith('•')) {
      // Parse bold text within the line
      const formattedLine = line.split('**').map((part, i) => {
        if (i % 2 === 1) {
          // Odd indexes are bold
          return <strong key={i}>{part}</strong>;
        }
        return part;
      });
      return (
        <div key={index} className="formatted-bullet-point">
          {formattedLine}
        </div>
      );
    }

    // Full line bold (section headers)
    else if (line.startsWith('**') && line.endsWith('**')) {
      const boldText = line.slice(2, -2);
      return (
        <div key={index} className="formatted-section-header">
          {boldText}
        </div>
      );
    }

    // Line with some bold text
    else if (line.includes('**')) {
      const formattedLine = line.split('**').map((part, i) => {
        if (i % 2 === 1) {
          return <strong key={i}>{part}</strong>;
        }
        return part;
      });
      return (
        <div key={index} className="formatted-text-line">
          {formattedLine}
        </div>
      );
    }

    // Empty line for spacing
    else if (line.trim() === '') {
      return <div key={index} className="formatted-spacer" />;
    }

    // Regular line
    return (
      <div key={index} className="formatted-regular-line">
        {line}
      </div>
    );
  });
};

/**
 * Wrapper component for formatted content
 * @param content - The text content to format
 * @param className - Optional CSS class name for the wrapper
 * @returns React component with formatted content
 */
export const FormattedContent: React.FC<{ content: string; className?: string }> = ({ content, className = '' }) => {
  return (
    <div className={`formatted-content ${className}`}>
      {formatContent(content)}
    </div>
  );
};

export default formatContent;